import { createFileRoute, useNavigate } from "@tanstack/react-router";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ReplayIcon from "@mui/icons-material/ReplayRounded";
import { PageContainer, PageHeader } from "@/components/layout/page";
import { Cluster } from "@/components/layout/cluster";
import { Breadcrumbs } from "@/components/nav/breadcrumbs";
import { StatusChip } from "@/components/display/status-chip";
import { RelativeTime } from "@/components/display/relative-time";
import { IdChip } from "@/components/display/mono";
import { LoadingState, ErrorState } from "@/components/feedback/states";
import { useToast } from "@/components/feedback/toaster";
import { JsonViewer } from "@/components/display/json-viewer";
import { TraceViewer } from "@/components/monitoring/trace-viewer";
import { DiagnosePanel } from "@/components/ai/diagnose-panel";
import { ExplainPanel } from "@/components/ai/explain-panel";
import { executionDetailQuery, useExecutionDetail, usePipelineMutations } from "@/api/pipelines";
import { useDeadLetters } from "@/api/executions";
import { useLiveInvalidate } from "@/lib/live";
import { fmtDuration, fmtNumber } from "@/lib/format";

export const Route = createFileRoute("/_app/pipelines/$pipelineId/runs/$runId")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(executionDetailQuery(params.pipelineId, params.runId)),
  component: RunDetail,
});

const REPLAYABLE = new Set(["FAILED", "STALE", "INTERRUPTED", "TIMED_OUT", "PARTIAL"]);

function RunDetail() {
  const { pipelineId, runId } = Route.useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { data: e, isLoading, error, refetch } = useExecutionDetail(pipelineId, runId);
  const dlq = useDeadLetters(runId);
  const m = usePipelineMutations(pipelineId);
  useLiveInvalidate([["pipelines", "execution-detail", pipelineId, runId]]);

  if (isLoading) return <LoadingState />;
  if (error || !e) return <ErrorState error={error ?? "Not found"} onRetry={() => refetch()} />;

  const running = ["RUNNING", "QUEUED", "STARTING"].includes(e.status.toUpperCase());
  const steps = e.steps ?? [];
  const stepsDone = steps.filter((s) =>
    ["SUCCESS", "COMPLETED", "SKIPPED", "FAILED"].includes((s.status ?? "").toUpperCase()),
  ).length;

  return (
    <PageContainer maxWidth={900}>
      {running ? (
        <Box sx={{ mb: 2 }}>
          <Stack
            direction="row"
            sx={{ justifyContent: "space-between", mb: 0.5, alignItems: "baseline" }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Running
              {steps.length
                ? ` · step ${Math.min(stepsDone + 1, steps.length)} of ${steps.length}`
                : ""}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {fmtNumber(e.recordsRead)} read · {fmtNumber(e.recordsWritten)} written
            </Typography>
          </Stack>
          <LinearProgress
            variant={steps.length ? "determinate" : "indeterminate"}
            value={steps.length ? (stepsDone / steps.length) * 100 : undefined}
          />
        </Box>
      ) : null}
      <PageHeader
        title="Run detail"
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: "Pipelines", to: "/pipelines" },
              { label: "Runs", to: `/pipelines/${pipelineId}/runs` },
              { label: runId.slice(0, 8) },
            ]}
          />
        }
        actions={
          REPLAYABLE.has(e.status.toUpperCase()) ? (
            <Button
              startIcon={<ReplayIcon />}
              variant="contained"
              onClick={async () => {
                const res = await m.replay.mutateAsync({ pid: pipelineId, execId: runId });
                toast.success("Replay started");
                navigate({
                  to: "/pipelines/$pipelineId/runs/$runId",
                  params: { pipelineId, runId: res.executionId },
                });
              }}
            >
              Replay
            </Button>
          ) : null
        }
      />

      <Card sx={{ p: 2.5, mb: 3 }}>
        <Cluster gap={4} align="start">
          <Field label="Status">
            <StatusChip status={e.status} />
          </Field>
          <Field label="Trigger">{e.triggerType ?? "—"}</Field>
          <Field label="Records read">{fmtNumber(e.recordsRead)}</Field>
          <Field label="Records written">{fmtNumber(e.recordsWritten)}</Field>
          <Field label="Records failed">{fmtNumber(e.recordsFailed)}</Field>
          <Field label="Duration">{fmtDuration(e.durationMs)}</Field>
          <Field label="Started">
            <RelativeTime value={e.startedAt} />
          </Field>
          <Field label="Trace ID">
            <IdChip value={runId} truncate={12} />
          </Field>
        </Cluster>
      </Card>

      {e.errorMessage ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {e.errorMessage}
        </Alert>
      ) : null}

      {["FAILED", "STALE", "INTERRUPTED", "TIMED_OUT", "PARTIAL"].includes(
        e.status.toUpperCase(),
      ) ? (
        <DiagnosePanel
          executionId={runId}
          pipelineId={pipelineId}
          onAction={async (kind) => {
            if (kind === "REPLAY" && REPLAYABLE.has(e.status.toUpperCase())) {
              const res = await m.replay.mutateAsync({ pid: pipelineId, execId: runId });
              toast.success("Replay started");
              navigate({
                to: "/pipelines/$pipelineId/runs/$runId",
                params: { pipelineId, runId: res.executionId },
              });
            }
          }}
        />
      ) : null}

      <ExplainPanel executionId={runId} pipelineId={pipelineId} />

      <Typography variant="h3" sx={{ mb: 1.5 }}>
        Steps
      </Typography>
      <Card sx={{ p: 2 }}>
        {(e.steps ?? []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No per-step detail recorded for this run.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {(e.steps ?? []).map((s, i) => (
              <Stack key={s.id ?? i} direction="row" spacing={2} sx={{ alignItems: "center" }}>
                <StatusChip status={s.status ?? "UNKNOWN"} dot={false} />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {s.stepName ?? s.stepId ?? `Step ${i + 1}`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {fmtNumber(s.recordsIn)} → {fmtNumber(s.recordsOut)} · {fmtDuration(s.durationMs)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Card>

      <Typography variant="h3" sx={{ mt: 3, mb: 1.5 }}>
        Trace
      </Typography>
      <Card sx={{ p: 2 }}>
        <TraceViewer executionId={runId} />
      </Card>

      {(dlq.data?.content.length ?? 0) > 0 ? (
        <>
          <Typography variant="h3" sx={{ mt: 3, mb: 1.5 }}>
            Dead letters ({dlq.data?.totalElements})
          </Typography>
          <Stack spacing={1.5}>
            {(dlq.data?.content ?? []).map((d) => (
              <Card key={d.id} sx={{ p: 2 }}>
                <Cluster gap={2} sx={{ mb: 1 }}>
                  <StatusChip status="SKIPPED" dot={false} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {d.stepName ?? d.phase ?? "—"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {d.errorClass}
                  </Typography>
                </Cluster>
                {d.errorMessage ? (
                  <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                    {d.errorMessage}
                  </Typography>
                ) : null}
                {d.recordData ? (
                  <JsonViewer value={safeParse(d.recordData)} maxHeight={160} />
                ) : null}
              </Card>
            ))}
          </Stack>
        </>
      ) : null}
    </PageContainer>
  );
}

function safeParse(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return s;
  }
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
        {label}
      </Typography>
      <Box sx={{ fontSize: "0.875rem" }}>{children}</Box>
    </Box>
  );
}
